import { useState, useRef, useEffect } from 'react';
import type { PurchaseHistory } from '../modules/DataTransformSchema';
import { useAnalytics } from '../hooks/useAnalytics';
import { convertToCSV } from '../utils/csvConverter';

type DownloadButtonProps = {
  orders: PurchaseHistory[];
  disabled?: boolean;
};

export function DownloadButton({
  orders,
  disabled = false,
}: DownloadButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { track } = useAnalytics();

  const triggerDownload = (
    content: string,
    filename: string,
    mimeType: string
  ) => {
    const dataBlob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadJSON = () => {
    track('Downloads Initiated', {
      format: 'json',
      itemCount: orders.length,
    });

    const ordersForExport = orders.map((order) => ({
      ...order,
      order_date:
        order.order_date instanceof Date
          ? order.order_date.toISOString().split('T')[0]
          : order.order_date,
    }));

    const dataStr = JSON.stringify(ordersForExport, null, 2);
    const filename = `purchases_${new Date().toISOString().split('T')[0]}.json`;
    triggerDownload(dataStr, filename, 'application/json');

    setIsOpen(false);
  };

  const downloadCSV = () => {
    track('Downloads Initiated', {
      format: 'csv',
      itemCount: orders.length,
    });

    const csvData = convertToCSV(orders);
    const filename = `purchases_${new Date().toISOString().split('T')[0]}.csv`;
    triggerDownload(csvData, filename, 'text/csv;charset=utf-8;');

    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        data-testid="download-button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-[13px] font-semibold transition-colors ${
          disabled
            ? 'cursor-not-allowed border-rule-light bg-paper text-ink-faint'
            : 'cursor-pointer border-rule bg-surface text-ink hover:border-primary hover:text-primary'
        }`}
      >
        <svg
          className="w-4 h-4"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 15V3" />
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <path d="m7 10 5 5 5-5" />
        </svg>
        Download
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-10 mt-2 w-48 overflow-hidden rounded-2xl border border-rule-light bg-surface p-1.5 shadow-[0_12px_32px_rgba(0,0,0,0.12)]">
          <button
            data-testid="download-csv-option"
            onClick={downloadCSV}
            className="w-full cursor-pointer rounded-[10px] px-3 py-2 text-left text-[13px] font-medium text-ink transition-colors hover:bg-primary-pale hover:text-primary"
          >
            Download as CSV
          </button>
          <button
            data-testid="download-json-option"
            onClick={downloadJSON}
            className="w-full cursor-pointer rounded-[10px] px-3 py-2 text-left text-[13px] font-medium text-ink transition-colors hover:bg-primary-pale hover:text-primary"
          >
            Download as JSON
          </button>
        </div>
      )}
    </div>
  );
}
